import { Dispatch, SetStateAction, createContext, useContext, useEffect, useState, ReactNode } from "react";
import { useLazyGetCompaniesQuery, useCompanyApiPrefetch } from "services/companyApi";
import { Company, Order } from "helpers";
import { CompanyResponse } from "services";
import { FilterItem } from "components/Table";

interface Params {
  search?: string;
  sort?: string;
  order?: Order;
  status?: string;
}

const initialParams: Params = {
  search: undefined,
  sort: undefined,
  order: undefined,
  status: undefined,
};

export interface CompanyManagementContextValues {
  companies: Company[] | undefined;
  data: CompanyResponse | undefined;
  isLoading: boolean;
  isFetching: boolean;
  isSuccess: boolean;
  params: Params;
  page: number;
  setPage: Dispatch<SetStateAction<number>>;
  onSort: (sort: string, order?: Order) => void;
  onSearch: (search: string) => void;
  onFilterByStatus: (status?: FilterItem) => void;
  onResetFilters: () => void;
  onPrefetchNext: (page: number) => void;
  onFetch: (preferCacheValue?: boolean) => Promise<void>;
}

export const CompanyManagementContext = createContext<CompanyManagementContextValues | undefined>(undefined);

export const CompanyManagementProvider = ({ children }: { children: ReactNode }) => {
  const [trigger, result] = useLazyGetCompaniesQuery();
  const prefetchCompanies = useCompanyApiPrefetch("getCompanies");
  const [params, setParams] = useState<Params>(initialParams);
  const [page, setPage] = useState<number>(0);

  const pageSize = 5;

  useEffect(() => {
    onFetchHandler(true);
  }, [params, page]);

  const onSortHandler = (sort: string, order?: Order) => {
    setPage(0);
    setParams((oldParams) => ({ ...oldParams, sort, order }));
  };

  const onSearchHandler = (search: string) => {
    setPage(0);
    setParams(() => ({ ...initialParams, search }));
  };

  const onFilterByStatusHandler = (status?: FilterItem) => {
    setPage(0);
    setParams((oldParams) => ({
      ...oldParams,
      status: status ? String(status) : undefined,
    }));
  };

  const onResetFiltersHandler = () => {
    setPage(0);
    setParams(initialParams);
  };

  const onPrefetchNextHandler = (nextPage: number) => {
    if (result?.data?.totalPages && nextPage < result.data.totalPages) {
      prefetchCompanies({ page: nextPage, pageSize, ...params }, { ifOlderThan: 5 });
    }
  };
  const onFetchHandler = async (preferCacheValue: boolean = false) => {
    try {
      await trigger({ page, pageSize, ...params }, preferCacheValue);
    } catch (error) {
      console.error("Error fetching companies:", error);
    }
  };

  const values: CompanyManagementContextValues = {
    companies: result?.data?.companies,
    data: result?.data,
    isLoading: result.isLoading,
    isFetching: result.isFetching,
    isSuccess: result.isSuccess,
    params,
    page,
    setPage,
    onSort: onSortHandler,
    onSearch: onSearchHandler,
    onFilterByStatus: onFilterByStatusHandler,
    onResetFilters: onResetFiltersHandler,
    onPrefetchNext: onPrefetchNextHandler,
    onFetch: onFetchHandler,
  };

  return <CompanyManagementContext.Provider value={values}>{children}</CompanyManagementContext.Provider>;
};

export const useCompanyManagementContext = () => {
  const context = useContext(CompanyManagementContext);

  if (!context) {
    throw new Error("useCompanyManagementContext must be used within a CompanyManagementProvider!");
  }
  return context;
};
